import { ReactNode } from 'react'

import { MNormal } from '@avantstay/backoffice-core'

import { PermittingInfoProps } from '@arriere/types/Properties.types'

import {
  formatBooleanField,
  formatDateField,
  formatDateTimeField,
} from './PropertyPolicyAndPermits.utils'

export enum PropertyPolicyAndPermitsSections {
  permitting = 'permitting',
  listings = 'listings',
  businessLicenseInformation = 'business-license-information',
  tot = 'tot',
  permitInspections = 'permit-inspections',
}

type Fields = {
  [K in keyof PermittingInfoProps]: {
    key: K
    title: string
    afterTitle?: ReactNode
    tooltipContent?: string
    format?: (value: PermittingInfoProps[K]) => ReactNode
  }
}[keyof PermittingInfoProps][]

type PropertyPolicyAndPermitsSectionsMapper = {
  key: PropertyPolicyAndPermitsSections
  title: string
  fields: Fields
}

export const propertyPolicyAndPermitsFixedFields: Fields = [
  {
    key: 'updatedAt',
    title: 'Updated at',
    format: formatDateTimeField,
  },
  {
    key: 'updatedBy',
    title: 'Updated by',
    format: value => value?.name,
  },
]

export const propertyPolicyAndPermitsSectionsMapper: PropertyPolicyAndPermitsSectionsMapper[] = [
  {
    key: PropertyPolicyAndPermitsSections.permitting,
    title: 'Permitting',
    fields: [
      {
        key: 'strPermitNumber',
        title: 'STR Permit Number',
        tooltipContent:
          'When a permit is required, this field will populate once we have received the permit.',
      },
      {
        key: 'strPermitExpiration',
        title: 'STR Permit Expiry Date',
        tooltipContent:
          'Date of current permit expiration; if date has passed, we have not yet recieved the new permit.',
        format: formatDateField,
      },
      {
        key: 'standardPermitApplicationDate',
        title: 'New Permit Submitted Date',
        tooltipContent: 'Date that the application for a new standard permit was submitted.',
        format: formatDateField,
      },
      {
        key: 'renewalPermitApplicationDate',
        title: 'Renewal Permit Application Submitted Date',
        tooltipContent:
          'Date Regulatory Affairs team member submitted the renewal permit application to the jurisdiction.',
        format: formatDateField,
      },
      {
        key: 'estimatedPermittingTimeline',
        title: 'Estimated Permitting Timeline (Days)',
        tooltipContent:
          'Estimate of how long it will take to obtain an initial permit in the associated jurisdiction.',
      },
      {
        key: 'canGoLiveWithoutPermit',
        title: 'Can go live without a permit?',
        tooltipContent:
          '"Yes" means we are okay to list this home as a short-term rental before we receive the permit.',
        format: value => formatBooleanField('canGoLiveWithoutPermit', value),
      },
      {
        key: 'canOperateDuringRenewal',
        title: 'Okay to operate during renewal?',
        tooltipContent:
          '"Yes" means that if the application has been submitted, then we can continue operating even if the expiration date has passed. "No" means we must adjust the liting to LTR if the expiration date has passed.',
        format: value => formatBooleanField('canOperateDuringRenewal', value),
      },
    ],
  },
  {
    key: PropertyPolicyAndPermitsSections.listings,
    title: 'Listings',
    fields: [
      {
        key: 'isLtrOnly',
        title: 'LTR Only?',
        format: value => formatBooleanField('isLtrOnly', value),
      },
      {
        key: 'isPermitRequiredOnListing',
        title: 'Permit # Required in Listings?',
        format: value => formatBooleanField('isPermitRequiredOnListing', value),
      },
      { key: 'listingRequirements', title: 'Listing Requirements' },
      { key: 'jurisdictionStrMinNights', title: 'Jurisdiction STR Minimum Night' },
      {
        key: 'jurisdictionLtrMinNights',
        title: 'Jurisdiction LTR Minimum Night',
        afterTitle: (
          <MNormal as="i">
            (NOTE: when present, this will always supersede 'Jurisdiction STR Minimum Night')
          </MNormal>
        ),
      },
      { key: 'jurisdictionMaxStays', title: 'Jurisdiction Maximum # of Stays' },
      {
        key: 'jurisdictionAdditionalRestrictions',
        title: 'Jurisdiction Additional Stay Restrictions',
      },
      {
        key: 'propertyMaxStays',
        title: 'Property Maximum # of Stays',
      },
      {
        key: 'propertyAdditionalRestrictions',
        title: 'Property Additional Stay Restrictions',
      },
    ],
  },
  {
    key: PropertyPolicyAndPermitsSections.businessLicenseInformation,
    title: 'Business License Information',
    fields: [
      {
        key: 'businessLicense',
        title: 'Business License #',
        tooltipContent:
          'When a busines license is required, this field will populate once we have received the license.',
      },
      {
        key: 'businessLicenseExpiration',
        title: 'Business License Expiration',
        tooltipContent:
          'Date of current business license expiration; if date has passed, we have not yet recieved the new license.',
        format: formatDateField,
      },
    ],
  },
  {
    key: PropertyPolicyAndPermitsSections.tot,
    title: 'TOT',
    fields: [
      {
        key: 'totCertificateNumber',
        title: 'TOT Certificate #',
        tooltipContent:
          'When a tax certificate is required, this field will populate once we have received the certificate.',
      },
    ],
  },
  {
    key: PropertyPolicyAndPermitsSections.permitInspections,
    title: 'Permit Inspections',
    fields: [
      {
        key: 'interiorInspectionExpiration',
        title: 'Interior Inspection Expiration',
        tooltipContent:
          'The interior inspection must be completed by this date in order to maintain the short-term rental permit.',
        format: formatDateField,
      },
      {
        key: 'exteriorInspectionExpiration',
        title: 'Exterior Inspection Expiration',
        tooltipContent:
          'The exterior inspection must be completed by this date in order to maintain the short-term rental permit.',
        format: formatDateField,
      },
      {
        key: 'additionalInspectionExpiration',
        title: 'Additional Inspection Expiration',
        tooltipContent:
          'Any additional inspections must be completed by this date in order to maintain the short-term rental permit.',
        format: formatDateField,
      },
    ],
  },
]
